import React from 'react';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 2.5),
    gap: theme.spacing(2),
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(12px)',
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  centerRoot: {
    justifyContent: 'center',
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500,
    '& strong': {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
  },
  controlsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  navBtn: {
    minWidth: 36,
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(16,185,129,0.1)',
      borderColor: '#10b981',
      color: '#10b981',
    },
    '&:disabled': {
      borderColor: theme.palette.divider,
      color: theme.palette.text.disabled,
      opacity: 0.4,
    },
  },
  pageBtn: {
    minWidth: 36,
    height: 36,
    padding: '0 6px',
    borderRadius: 10,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    background: 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(16,185,129,0.12)',
      borderColor: '#10b981',
      color: '#10b981',
    },
  },
  activePageBtn: {
    minWidth: 36,
    height: 36,
    padding: '0 6px',
    borderRadius: 10,
    fontSize: '0.875rem',
    fontWeight: 700,
    color: '#ffffff !important',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%) !important',
    border: '1px solid #10b981',
    boxShadow: '0 2px 8px rgba(16,185,129,0.4)',
    cursor: 'default',
  },
  dots: {
    color: theme.palette.text.secondary,
    padding: '0 4px',
    fontWeight: 700,
    userSelect: 'none',
    letterSpacing: 2,
  },
}));

function getPageNumbers(current, total) {
  if (!total || total <= 1) return [1];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, '...', total];
  }
  if (current >= total - 3) {
    return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, '...', current - 1, current, current + 1, '...', total];
}

export default function Pagination({
  page = 1,
  totalPages = 1,
  onPageChange,
  totalCount,
  pageSize = 20,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}) {
  const classes = useStyles();

  // Handle compatibility if totalPages not given but hasNext/hasPrev are
  const computedTotalPages = totalPages || (hasNext ? page + 1 : page);
  const pages = getPageNumbers(page, computedTotalPages);

  const canPrev = hasPrev !== undefined ? hasPrev : page > 1;
  const canNext = hasNext !== undefined ? hasNext : page < computedTotalPages;

  const handlePrevClick = () => {
    if (onPrev) {
      onPrev();
    } else if (onPageChange && page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNextClick = () => {
    if (onNext) {
      onNext();
    } else if (onPageChange && page < computedTotalPages) {
      onPageChange(page + 1);
    }
  };

  const handlePageClick = (p) => {
    if (typeof p === 'number' && p !== page && onPageChange) {
      onPageChange(p);
    }
  };

  // If there is only 1 page and no totalCount, don't necessarily hide, but render cleanly
  if (computedTotalPages <= 1 && !totalCount) {
    return null;
  }

  const startItem = totalCount ? (page - 1) * pageSize + 1 : null;
  const endItem = totalCount ? Math.min(page * pageSize, totalCount) : null;

  return (
    <Box className={`${classes.root} ${!totalCount ? classes.centerRoot : ''}`}>
      {totalCount !== undefined && totalCount > 0 && (
        <Typography className={classes.infoText}>
          Showing <strong>{startItem} - {endItem}</strong> of <strong>{totalCount}</strong> items
        </Typography>
      )}

      <Box className={classes.controlsWrapper}>
        <Button
          variant="outlined"
          size="small"
          className={classes.navBtn}
          disabled={!canPrev}
          onClick={handlePrevClick}
          aria-label="Previous Page"
        >
          <ChevronLeftIcon fontSize="small" />
        </Button>

        {pages.map((p, idx) => {
          if (p === '...') {
            return (
              <Typography key={`dots-${idx}`} className={classes.dots}>
                ...
              </Typography>
            );
          }
          const isActive = p === page;
          return (
            <Button
              key={`page-${p}`}
              variant="outlined"
              size="small"
              className={isActive ? classes.activePageBtn : classes.pageBtn}
              onClick={() => handlePageClick(p)}
            >
              {p}
            </Button>
          );
        })}

        <Button
          variant="outlined"
          size="small"
          className={classes.navBtn}
          disabled={!canNext}
          onClick={handleNextClick}
          aria-label="Next Page"
        >
          <ChevronRightIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
}